type LoggedCallbackStatus = 'called' | 'succeeded' | 'failed'

type LoggedCallbackEvent = {
  error?: unknown
  name: string
  status: LoggedCallbackStatus
}

type LogCallbackEvent = (event: LoggedCallbackEvent) => void
type WrappedFunction = (...args: unknown[]) => unknown

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    value != null &&
    typeof value === 'object' &&
    'then' in value &&
    typeof value.then === 'function'
  )
}

/** Add logging to all function properties of an object.
 * Logs when the function is called and when it succeeds or fails.
 */
export function useLoggedCallbacks<T extends object>(
  target: T,
  log: LogCallbackEvent,
): T {
  return new Proxy(target, {
    get(object, property) {
      const value = object[property as keyof T]

      if (typeof value !== 'function') {
        return value
      }

      const name = String(property)

      return (...args: unknown[]) => {
        log({ name, status: 'called' })

        try {
          const result = (value as WrappedFunction).apply(object, args)

          if (isPromiseLike(result)) {
            return result.then(
              (resolvedValue) => {
                log({ name, status: 'succeeded' })

                return resolvedValue
              },
              (error: unknown) => {
                log({ error, name, status: 'failed' })
                throw error
              },
            )
          }

          log({ name, status: 'succeeded' })

          return result
        } catch (error) {
          log({ error, name, status: 'failed' })
          throw error
        }
      }
    },
  })
}
